/* Megamenu Link List — vanilla port of
 * orion-v3.0/src/js/includes/megamenu.js.
 *
 * Each .megamenu-list-js block is a 3-tier hover/focus navigation
 * (main → sub → sub-sub). The Liquid template renders all three
 * tiers in a shared 1-row grid so only the panel matching the
 * currently active link is visible at a time (via .is-active +
 * CSS opacity transitions).
 *
 * Data attributes consumed (set by blocks/_megamenu-linklist.liquid):
 *   data-default-main      Handle of the first top-level menu item;
 *                           used as the "preselect" target when the
 *                           merchant has show_sublist on.
 *   data-show-sublist      'true' | 'false' — gates the initial
 *                           activation pass.
 *   data-mega-main         A top-level menu link.
 *   data-mega-panel        Container holding one main link's children.
 *   data-nested            A sub-link.
 *   data-has-grandchildren On a sub-link that has its own children.
 *   data-mega-sub          Container holding one sub-link's children.
 *
 * Initialisation is idempotent — calling init() on an already-bound
 * .megamenu-list-js is a no-op (guarded by `data-initialized`). */

class MegamenuLinkList {
  constructor(root) {
    this.root = root;
    this.mainLinks = Array.from(root.querySelectorAll('[data-mega-main]'));
    this.panels = Array.from(root.querySelectorAll('[data-mega-panel]'));
    this.subLinks = Array.from(root.querySelectorAll('[data-nested]'));
    this.subPanels = Array.from(root.querySelectorAll('[data-mega-sub]'));
    this.defaultMain = root.dataset.defaultMain;
    this.showSublist = root.dataset.showSublist === 'true';
  }

  init() {
    if (this.root.dataset.initialized === 'true') return;

    this.mainLinks.forEach((link) => {
      const activate = () => this.activateMain(link.dataset.megaMain);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    this.subLinks.forEach((link) => {
      const activate = () => {
        // Sub-link with grandchildren reveals its sub-sub panel;
        // sub-link without grandchildren clears whatever sub-sub
        // panel is currently visible (handed null → deactivate).
        const handle = link.dataset.hasGrandchildren === 'true'
          ? link.dataset.nested
          : null;
        this.activateSubPanel(handle);
      };
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    // Preselect the cached main link so the deepest panel is visible
    // straight away when the megamenu opens (only when show_sublist
    // is on — otherwise there's nothing to preselect).
    const initialMain = this.defaultMain
      || (this.mainLinks[0] && this.mainLinks[0].dataset.megaMain);
    if (initialMain && this.showSublist) {
      this.activateMain(initialMain);
    }

    this.root.dataset.initialized = 'true';
  }

  deactivateSubPanels() {
    this.subPanels.forEach((panel) => panel.classList.remove('is-active'));
    this.subLinks.forEach((link) => link.classList.remove('is-active'));
  }

  activateSubPanel(handle) {
    if (!handle) {
      this.deactivateSubPanels();
      return;
    }
    this.subPanels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaSub === handle);
    });
    this.subLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.nested === handle);
    });
  }

  activateMain(handle) {
    if (!handle) return;

    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaPanel === handle);
    });
    this.mainLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.megaMain === handle);
    });

    // Always clear any sub-panel + sub-link state when switching to
    // a different main. Column 3 only appears when the user
    // explicitly hovers / focuses a sub-link that has grandchildren.
    this.deactivateSubPanels();
  }
}

function start() {
  document.querySelectorAll('.megamenu-list-js').forEach((root) => {
    new MegamenuLinkList(root).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
